import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// localStorage key for the persisted Supabase access token. The backend
// returns `session_token` in the body of every auth response and also
// accepts an Authorization: Bearer header (server.py: get_current_user).
// We persist + attach the token here so authentication works even when
// browsers block third-party cookies in cross-domain deployments
// (e.g. frontend on app.ripperstennis.com calling the API on
// tennis-ladder.emergent.host). withCredentials remains true so cookies
// continue to work in same-origin contexts (e.g. the dev preview).
const TOKEN_KEY = 'tl_session_token';

export const tokenStore = {
  get: () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set: (t) => {
    try { if (t) localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
  },
  clear: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
};

// Storage key used to keep the pending-approval screen stable across the
// sign-out boundary. Cleared on logout from this page is intentional —
// see RegistrationPendingPage / authAPI.logout. Anything more durable
// would let an attacker spoof "your" pending screen on a shared device.
export const PENDING_REG_KEY = 'tl_pending_registration';

export const pendingRegistrationStore = {
  get: () => {
    try {
      const raw = localStorage.getItem(PENDING_REG_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  set: (data) => {
    try { localStorage.setItem(PENDING_REG_KEY, JSON.stringify(data)); } catch { /* ignore */ }
  },
  clear: () => {
    try { localStorage.removeItem(PENDING_REG_KEY); } catch { /* ignore */ }
  },
};

// Impersonation UI/convenience state. Holds the impersonated member's display
// name + the server-signed return ticket used to exit back to the admin.
// The ticket is NOT a credential by itself — the backend re-verifies admin
// role on /admin/impersonate/stop. Kept in localStorage so the banner + exit
// survive reloads while impersonating.
export const IMPERSONATION_KEY = 'tl_impersonation';
export const impersonationStore = {
  get: () => {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  set: (data) => {
    try { localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data)); } catch { /* ignore */ }
  },
  clear: () => {
    try { localStorage.removeItem(IMPERSONATION_KEY); } catch { /* ignore */ }
  },
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Outgoing: attach the persisted Bearer token (if any) on every request.
api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Incoming: capture session_token from any auth-response body and persist
// it. Any of /auth/login, /auth/signup, /auth/session, /auth/redeem-invite
// will return one — we just snag it once it appears.
//
// Also: when the backend returns 401 on a request that DID carry a token,
// the user's session has expired. Clear the dead token and fire a global
// `tl:session-expired` event so AuthContext can clean up state + redirect
// to /login?reason=timeout. We deliberately skip the redirect for 401s on
// unauth'd requests (e.g. the initial /auth/me probe before login, or a
// failed /auth/login attempt with bad credentials).
api.interceptors.response.use(
  (resp) => {
    const t = resp?.data?.session_token;
    if (t) tokenStore.set(t);
    return resp;
  },
  (err) => {
    const status = err?.response?.status;
    const reqHadToken = !!err?.config?.headers?.Authorization;
    if (status === 401 && reqHadToken) {
      tokenStore.clear();
      try {
        window.dispatchEvent(new CustomEvent('tl:session-expired'));
      } catch { /* ignore */ }
    }
    return Promise.reject(err);
  },
);

// Auth
export const authAPI = {
  signup: (name, email, password) => api.post('/auth/signup', { name, email, password }),
  register: (data) => api.post('/auth/register', data),
  // Phase C1 — Membership 2.0: public SMS-verified registration + reset
  registerStart:   (data)  => api.post('/auth/register/start', data),
  registerVerify:  (data)  => api.post('/auth/register/verify', data),
  resetStart:      (data)  => api.post('/auth/password-reset/start', data),
  resetVerify:     (data)  => api.post('/auth/password-reset/verify', data),
  resetComplete:   (data)  => api.post('/auth/password-reset/complete', data),
  verifyStatus:    ()      => api.get('/auth/verify-service-status'),
  login: (email, password) => api.post('/auth/login', { email, password }),
  exchangeSession: (sessionId) => api.get(`/auth/session?session_id=${sessionId}`),
  redeemInvite: (inviteCode) => api.post('/auth/redeem-invite', { invite_code: inviteCode }),
  completeInvite: (payload) => api.post('/auth/complete-invite', payload),
  validateInvite: (code) => api.get(`/invites/validate/${encodeURIComponent(code)}`),
  getMe: () => api.get('/auth/me'),
  logout: async () => {
    try { await api.post('/auth/logout'); } finally { tokenStore.clear(); impersonationStore.clear(); }
  },
  recoverPassword: (email, redirectTo) => api.post('/auth/recover', { email, redirect_to: redirectTo }),
  // Manual-player claim flow — public endpoints
  validateClaimToken: (token) => api.get('/claim-account/validate', { params: { token } }),
  completeClaim: (payload) => api.post('/claim-account/complete', payload),
};

// Ladder
//
// Phase 1 multi-ladder: every endpoint accepts an optional ladder slug.
// All defaults preserve back-compat with the original single-ladder API
// by targeting 'singles-ladder' (the renamed original ladder).
export const ladderAPI = {
  listLadders: () => api.get('/ladders'),
  getAll: (slug = null) => api.get(slug ? `/ladder?ladder=${encodeURIComponent(slug)}` : '/ladder'),
  getAllIncludingPaused: (slug = null) => api.get(slug ? `/ladder/all?ladder=${encodeURIComponent(slug)}` : '/ladder/all'),
  // Approved user self-joins a ladder. Defaults to singles-ladder when no slug.
  join: (slug = null) => api.post('/ladder/join', slug ? { ladder: slug } : {}),
};

// Players
export const playerAPI = {
  get: (playerId) => api.get(`/players/${playerId}`),
  pause: () => api.put('/players/pause'),
  resume: () => api.put('/players/resume'),
  getProfile: () => api.get('/players/profile'),
  updateProfile: (data) => api.put('/players/profile', data),
  updateNotificationPrefs: (data) => api.patch('/players/notification-preferences', data),
  getSmsStatus: () => api.get('/players/sms-status'),
  confirmSmsConsent: () => api.post('/players/sms-consent', { agreed: true }),
  updateEmail: (email) => api.put('/players/email', { email }),
  uploadAvatar: (file) => api.post('/players/avatar', file, {
    headers: { 'Content-Type': file.type || 'image/jpeg' },
  }),
  deleteAvatar: () => api.delete('/players/avatar'),
};

// Challenges
export const challengeAPI = {
  create: (challengedId) => api.post('/challenges', { challenged_id: challengedId }),
  accept: (id) => api.put(`/challenges/${id}/accept`),
  decline: (id) => api.put(`/challenges/${id}/decline`),
  cancel: (id) => api.put(`/challenges/${id}/cancel`),
  getMy: () => api.get('/challenges/my'),
};

// Matches
export const matchAPI = {
  submit: (challengeId, winnerId, score) => api.post(`/matches/${challengeId}/submit`, { winner_id: winnerId, score }),
  approve: (matchId) => api.put(`/matches/${matchId}/approve`),
  // `reject` removed — see Correct Score flow (submit a corrected score via matchAPI.submit instead).
  getMy: () => api.get('/matches/my'),
  getForChallenge: (challengeId) => api.get(`/matches/by-challenge/${challengeId}`),
};

// Admin
export const adminAPI = {
  createInvite: (data) => api.post('/admin/invites', data),
  getInvites: () => api.get('/admin/invites'),
  getPlayers: () => api.get('/admin/players'),
  getPendingUsers: () => api.get('/admin/pending-users'),
  getConsentAudit: () => api.get('/admin/consent-audit'),
  approveUser: (profileId) => api.post(`/admin/users/${profileId}/approve`),
  denyUser: (profileId, reason) => api.post(`/admin/users/${profileId}/deny`, { reason }),
  adjustRank: (playerId, newRank) => api.put(`/admin/players/${playerId}/rank`, { new_rank: newRank }),
  changeStatus: (playerId) => api.put(`/admin/players/${playerId}/status`),
  removePlayer: (playerId) => api.delete(`/admin/players/${playerId}`),
  cancelChallenge: (challengeId) => api.put(`/admin/challenges/${challengeId}/cancel`),
  editMatch: (matchId, data) => api.put(`/admin/matches/${matchId}/edit`, data),
  correctMatch: (matchId, data) => api.put(`/admin/matches/${matchId}/correct`, data),
  getChallenges: () => api.get('/admin/challenges'),
  getMatches: () => api.get('/admin/matches'),
  getDashboard: () => api.get('/admin/dashboard'),
  resetPassword: (playerId, newPassword) => api.put(`/admin/players/${playerId}/password`, { new_password: newPassword }),
  impersonate: (playerId) => api.post(`/admin/impersonate/${playerId}`),
  stopImpersonation: (returnTicket) => api.post('/admin/impersonate/stop', { return_ticket: returnTicket }),
  revokeInvite: (inviteId) => api.post(`/admin/invites/${inviteId}/revoke`),
  // Ladder membership tooling (Phase 1 multi-ladder)
  // Both endpoints default to 'singles-ladder' when no slug is given,
  // preserving back-compat with the original single-ladder admin UI.
  getProfilesWithoutPlayer: (slug = null) => api.get(slug ? `/admin/profiles-without-player?ladder=${encodeURIComponent(slug)}` : '/admin/profiles-without-player'),
  addPlayerToLadder: (profileId, slug = null) => api.post('/admin/players/add-to-ladder', slug ? { profile_id: profileId, ladder: slug } : { profile_id: profileId }),
  // Phase L-ADMIN1 — dedicated ladder-management endpoints. See
  // AdminLadderManagePage / AdminEventsHubPage.
  listLadders: () => api.get('/admin/ladders'),
  createLadder: (data) => api.post('/admin/ladders', data),
  getLadderRoster: (slug) => api.get(`/admin/ladders/${encodeURIComponent(slug)}/roster`),
  reorderLadder: (slug, playerIds, reason = null) =>
    api.post(`/admin/ladders/${encodeURIComponent(slug)}/reorder`,
             reason ? { player_ids: playerIds, reason } : { player_ids: playerIds }),
  // RIPPERS — full ladder lifecycle (edit / close / reopen / archive / restore / delete).
  updateLadder: (slug, data) => api.patch(`/admin/ladders/${encodeURIComponent(slug)}`, data),
  closeLadder: (slug) => api.post(`/admin/ladders/${encodeURIComponent(slug)}/close`),
  reopenLadder: (slug) => api.post(`/admin/ladders/${encodeURIComponent(slug)}/reopen`),
  archiveLadder: (slug) => api.post(`/admin/ladders/${encodeURIComponent(slug)}/archive`),
  restoreLadder: (slug) => api.post(`/admin/ladders/${encodeURIComponent(slug)}/restore`),
  deleteLadder: (slug) => api.delete(`/admin/ladders/${encodeURIComponent(slug)}`),
  // EM-1/EM-3a — per-session Event Manager assignments. Admin-only.
  listSessionManagers: (sessionId) => api.get(`/admin/sessions/${sessionId}/managers`),
  assignSessionManager: (sessionId, profileId) => api.post(`/admin/sessions/${sessionId}/managers`, { profile_id: profileId }),
  removeSessionManager: (sessionId, profileId) => api.delete(`/admin/sessions/${sessionId}/managers/${profileId}`),
  // Universal Event Manager (Feb 2026) — one API for every event kind.
  // event_kind ∈ {event_session, tournament, round_robin, club_event}.
  getEventManager: (eventKind, eventId) => api.get(`/events/${eventKind}/${eventId}/manager`),
  assignEventManager: (eventKind, eventId, profileId) =>
    api.post(`/events/${eventKind}/${eventId}/manager`, { profile_id: profileId }),
  removeEventManager: (eventKind, eventId) => api.delete(`/events/${eventKind}/${eventId}/manager`),
  removeOneEventManager: (eventKind, eventId, profileId) => api.delete(`/events/${eventKind}/${eventId}/manager/${profileId}`),
  listEventManagerCandidates: (q = '') => api.get(`/events/manager-candidates`, { params: q ? { q } : {} }),
  // Picker source for assigning Event Managers — approved non-admin profiles only.
  listApprovedProfiles: () => api.get('/admin/approved-profiles'),
  // Manual member/player MVP — admin creates a participant without a signup
  // flow. Returns the new profile so the caller can immediately register it.
  createManualPlayer: (payload) => api.post('/admin/manual-players', payload),
  // Manual-player claim access — issue a one-time claim link.
  generateClaimLink: (profileId, payload = {}) =>
    api.post(`/admin/manual-players/${profileId}/claim-link`, payload),
  // Communications / SMS
  getSmsSettings: () => api.get('/admin/settings/sms'),
  setSmsSettings: (smsMode) => api.put('/admin/settings/sms', { sms_mode: smsMode }),
  // ----- Phase M1: Members hub -----
  listMembers: () => api.get('/admin/members'),
  getMember: (profileId) => api.get(`/admin/members/${profileId}`),
  editMember: (profileId, payload) => api.patch(`/admin/members/${profileId}`, payload),
  sendMemberInvite: (profileId, sendMethod = 'sms') =>
    api.post(`/admin/members/${profileId}/send-invite`, { send_method: sendMethod }),
  setMemberStatus: (profileId, status) => api.patch(`/admin/members/${profileId}/status`, { status }),
  setMemberGender: (profileId, gender) => api.patch(`/admin/members/${profileId}/gender`, { gender }),
  bulkSetGender: (profileIds, gender, overrideExisting = false) =>
    api.post('/admin/members/bulk-set-gender', { profile_ids: profileIds, gender, override_existing: overrideExisting }),
  bulkDeactivate: (profileIds, reason = null) =>
    api.post('/admin/members/bulk-deactivate', { profile_ids: profileIds, reason }),
  deletePreview: (profileIds) =>
    api.post('/admin/members/delete-preview', { profile_ids: profileIds }),
  bulkDelete: (profileIds, reason = null) =>
    api.post('/admin/members/bulk-delete', { profile_ids: profileIds, reason, confirmed: true }),
  bulkExport: (profileIds) =>
    api.post('/admin/members/bulk-export', { profile_ids: profileIds }, { responseType: 'blob' }),
  regenerateInvite: (payload) => api.post('/admin/invites/regenerate', payload),
  resendInvite: (inviteId, sendMethod = 'sms') => api.post(`/admin/invites/${inviteId}/resend`, { send_method: sendMethod }),
  // ----- Admin Communications / Bulk SMS Center -----
  commsListEvents: () => api.get('/admin/comms/events'),
  commsPreview: (spec) => api.post('/admin/comms/preview', spec),
  commsSend: (spec) => api.post('/admin/comms/send', spec),
  commsHistory: () => api.get('/admin/comms/history'),
  commsHistoryDetail: (campaignId) => api.get(`/admin/comms/history/${campaignId}`),
  commsListSuppressions: () => api.get('/admin/comms/suppressions'),
  commsAddSuppression: (phone) => api.post('/admin/comms/suppressions', { phone }),
  commsRemoveSuppression: (phone) => api.delete(`/admin/comms/suppressions/${encodeURIComponent(phone)}`),
  debug: {
    expirePending: (challengeId) => api.post(`/admin/debug/expire-pending/${challengeId}`),
    expireAccepted: (challengeId) => api.post(`/admin/debug/expire-accepted/${challengeId}`),
    setInactive: (playerId) => api.post(`/admin/debug/set-inactive/${playerId}`),
    applyInactivity: (playerId) => api.post(`/admin/debug/apply-inactivity/${playerId}`),
    pausePlayer: (playerId) => api.post(`/admin/debug/pause-player/${playerId}`),
    resumePlayer: (playerId) => api.post(`/admin/debug/resume-player/${playerId}`),
    setPauseDuration: (playerId, days) => api.post(`/admin/debug/set-pause-duration/${playerId}`, { days }),
    integrityCheck: () => api.get('/admin/debug/integrity-check'),
    runExpiration: () => api.post('/admin/debug/run-expiration'),
  },
};

// Activity
export const activityAPI = {
  getFeed: () => api.get('/activity'),
};

// Universal member profile (Phase P1)
export const profileAPI = {
  get: (profileId) => api.get(`/profiles/${profileId || 'me'}`),
};

// Club Directory (Phase C1 Session 1.3)
export const directoryAPI = {
  list: () => api.get('/directory'),
};
